import { memo, useMemo, useState, type FC } from 'react';
import { StyleSheet, View } from 'react-native';
import { Divider, Text, TouchableRipple } from 'react-native-paper';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';

import { MEIN_ORT_LOCATION_BAR_HEIGHT } from '@/features/mein-ort/components/MeinOrtLocationBar';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { useMeinOrtTab } from '@/features/mein-ort/components/MeinOrtTabContext';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const LABEL_HEIGHT = 18;
const LABEL_FADE_DISTANCE = MEIN_ORT_LOCATION_BAR_HEIGHT;
const LABEL_GAP = 4;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surface,
      zIndex: 2,
      elevation: 2
    },
    row: {
      flexDirection: 'row',
      alignItems: 'stretch',
      paddingHorizontal: 8
    },
    tab: { flex: 1 },
    tabHover: {
      backgroundColor: theme.stateLayers.hover,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4
    },
    tabInner: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingVertical: 8
    },
    iconWrap: {
      width: 24,
      height: 24,
      alignItems: 'center',
      justifyContent: 'center'
    },
    dot: {
      position: 'absolute',
      top: -2,
      right: -10,
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: theme.colors.error
    },
    labelWrap: {
      marginTop: LABEL_GAP,
      height: LABEL_HEIGHT,
      alignSelf: 'stretch',
      overflow: 'hidden'
    },
    label: {
      fontSize: 14,
      lineHeight: LABEL_HEIGHT,
      fontWeight: '500',
      letterSpacing: 0.15,
      textAlign: 'center',
      color: theme.colors.primary
    },
    underline: {
      position: 'absolute',
      bottom: 0,
      left: 2,
      right: 2,
      height: 3,
      borderTopLeftRadius: 3,
      borderTopRightRadius: 3,
      backgroundColor: 'transparent'
    },
    underlineActive: { backgroundColor: theme.colors.primary },
    divider: { backgroundColor: theme.colors.outlineVariant }
  });

export const MeinOrtPageTabs: FC = memo(() => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [hovered, setHovered] = useState<string | undefined>();
  const { scrollY } = useMeinOrtScroll();
  const { channelConfigs, activeTab, activeChannel, onTabChange } =
    useMeinOrtTab();

  const activeChannelConfig = channelConfigs.find(
    (c) => c.key === activeChannel
  );

  const labelAnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    const progress = Math.min(
      Math.max(scrollY.value, 0) / LABEL_FADE_DISTANCE,
      1
    );
    return {
      opacity: 1 - progress,
      height: LABEL_HEIGHT * (1 - progress),
      marginTop: LABEL_GAP * (1 - progress)
    };
  });

  if (!activeChannelConfig) {
    return;
  }

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        {activeChannelConfig.tabs.map(({ key, label, icon: Icon, dot }) => {
          const active = key === activeTab;
          return (
            <TouchableRipple
              key={key}
              testID={`mein-ort-tab-${key}`}
              onPress={() => onTabChange(key)}
              onHoverIn={() => setHovered(key)}
              onHoverOut={() => setHovered((h) => (h === key ? undefined : h))}
              rippleColor={theme.rippleColors.onSurface}
              style={[styles.tab, hovered === key && styles.tabHover]}
              borderless
            >
              <View style={styles.tabInner}>
                <View style={styles.iconWrap}>
                  <Icon fill={theme.colors.primary} width={24} height={24} />
                  {dot ? <View style={styles.dot} /> : undefined}
                </View>
                <Animated.View style={[styles.labelWrap, labelAnimatedStyle]}>
                  <Text numberOfLines={1} style={styles.label}>
                    {label}
                  </Text>
                </Animated.View>
                <View
                  testID={active ? 'mein-ort-active-tab' : undefined}
                  style={[styles.underline, active && styles.underlineActive]}
                />
              </View>
            </TouchableRipple>
          );
        })}
      </View>
      <Divider style={styles.divider} />
    </View>
  );
});
MeinOrtPageTabs.displayName = 'MeinOrtPageTabs';
